import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Chip,
  useTheme,
  alpha,
  Divider,
  Alert,
  LinearProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Download,
  Computer,
  Apple,
  Computer as LinuxIcon,
  CheckCircle,
  GitHub,
  Terminal,
  Smartphone,
  Verified,
  Speed,
} from '@mui/icons-material';

const DownloadPage = () => {
  const theme = useTheme();
  const [detectedOS, setDetectedOS] = useState('');
  const [downloadStarted, setDownloadStarted] = useState(false);

  useEffect(() => {
    // Detect user's operating system
    const userAgent = window.navigator.userAgent;
    if (userAgent.indexOf('Win') !== -1) setDetectedOS('Windows');
    else if (userAgent.indexOf('Mac') !== -1) setDetectedOS('macOS');
    else if (userAgent.indexOf('Linux') !== -1) setDetectedOS('Linux');
    else setDetectedOS('Unknown');
  }, []);

  const handleDownload = (platform) => {
    setDownloadStarted(true);
    // In a real app, this would trigger the actual download
    setTimeout(() => setDownloadStarted(false), 3000);
  };

  const platforms = [
    {
      name: 'Windows',
      icon: <Computer sx={{ fontSize: 48 }} />,
      version: 'v1.2.0',
      size: '5.4 MB',
      format: 'GUI .exe',
      downloadUrl: '/releases/windows/installer/pwgen-gui-windows-x64.exe',
      recommended: detectedOS === 'Windows',
      requirements: 'Windows 10 or later',
      features: ['Graphical interface', 'Enhanced security', '40% smaller binaries'],
      cliVersion: {
        size: '4.5 MB',
        format: 'CLI .exe',
        downloadUrl: '/releases/windows/installer/pwgen-cli-windows-x64.exe',
      }
    },
    {
      name: 'macOS',
      icon: <Apple sx={{ fontSize: 48 }} />,
      version: 'v1.2.0',
      size: 'Coming Soon',
      format: '.dmg',
      downloadUrl: '#',
      recommended: detectedOS === 'macOS',
      requirements: 'macOS 11.0 or later',
      features: ['GUI interface', 'Native macOS integration', 'Coming in next release'],
      disabled: true,
    },
    {
      name: 'Linux',
      icon: <LinuxIcon sx={{ fontSize: 48 }} />,
      version: 'v1.2.0',
      size: '6.8 MB',
      format: 'AppImage',
      downloadUrl: '/releases/linux/appimage/PWGen-Rust-1.2.0-x86_64.AppImage',
      recommended: detectedOS === 'Linux',
      requirements: 'Ubuntu 20.04+ or equivalent',
      features: ['Portable application', 'Enhanced security', 'SHA-256 cryptography'],
      debVersion: {
        size: '4.4 MB',
        format: 'DEB Package',
        downloadUrl: '/releases/linux/deb/pwgen-rust_1.2.0_amd64.deb',
      },
      snapVersion: {
        size: 'Auto-updating',
        format: 'Snap Store',
        downloadUrl: 'https://snapcraft.io/pwgen-rust',
      }
    },
  ];

  const installMethods = [
    {
      title: 'Package Managers',
      icon: <Terminal sx={{ fontSize: 32 }} />,
      methods: [
        { name: 'Snap (Linux)', command: 'sudo snap install pwgen-rust', description: 'Install from Snap Store (recommended)' },
        { name: 'Cargo (All platforms)', command: 'cargo install --git https://github.com/KiefStudioMA/PWGen-Rust.git', description: 'Install from source via Rust' },
        { name: 'DEB Package (Linux)', command: 'wget https://pwgenrust.dev/releases/linux/deb/pwgen-rust_1.2.0_amd64.deb && sudo dpkg -i pwgen-rust_1.2.0_amd64.deb', description: 'Install DEB package' },
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', py: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            component="h1"
            fontWeight="bold"
            gutterBottom
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.accent?.main || theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
            }}
          >
            Download PWGen-Rust
          </Typography>
          <Typography variant="h5" color="text.secondary" paragraph>
            Get started with the most secure password manager built in Rust
          </Typography>
          
          {detectedOS && (
            <Alert 
              severity="info" 
              sx={{ 
                maxWidth: 500, 
                mx: 'auto', 
                mb: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                borderRadius: 2,
              }}
            >
              We detected you're using <strong>{detectedOS}</strong>. The recommended download is highlighted below.
            </Alert>
          )}

          {downloadStarted && (
            <Box sx={{ maxWidth: 400, mx: 'auto', mb: 4 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Starting download...
              </Typography>
              <LinearProgress color="primary" />
            </Box>
          )}
        </Box>

        {/* Platform Downloads */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {platforms.map((platform) => (
            <Grid size={{ xs: 12, md: 4 }} key={platform.name}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: platform.recommended ? `2px solid ${theme.palette.accent?.main || theme.palette.primary.main}` : '1px solid',
                  borderColor: platform.recommended ? (theme.palette.accent?.main || theme.palette.primary.main) : 'divider',
                  transform: platform.recommended ? 'scale(1.05)' : 'scale(1)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: platform.recommended ? 'scale(1.07)' : 'scale(1.02)',
                    boxShadow: 4,
                  },
                }}
              >
                {platform.recommended && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 20,
                      right: -35,
                      bgcolor: theme.palette.accent?.main || theme.palette.primary.main,
                      color: 'white',
                      px: 6,
                      py: 0.8,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      transform: 'rotate(45deg)',
                      transformOrigin: 'center',
                      boxShadow: 2,
                      zIndex: 2,
                      minWidth: 100,
                      textAlign: 'center'
                    }}
                  >
                    RECOMMENDED
                  </Box>
                )}
                
                <CardContent sx={{ flexGrow: 1, p: 4, textAlign: 'center' }}>
                  <Box sx={{ color: theme.palette.accent?.main || theme.palette.primary.main, mb: 3 }}>
                    {platform.icon}
                  </Box>
                  
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    {platform.name}
                  </Typography>
                  
                  <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3 }}>
                    <Chip label={platform.version} size="small" color="primary" />
                    <Chip label={platform.size} size="small" variant="outlined" />
                  </Stack>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {platform.requirements}
                  </Typography>
                  
                  <Box sx={{ mt: 3, mb: 4 }}>
                    {platform.features.map((feature, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                        <CheckCircle sx={{ fontSize: 16, color: 'success.main', mr: 1 }} />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                  </Box>
                  
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<Download />}
                    onClick={() => handleDownload(platform.name)}
                    component="a"
                    href={platform.downloadUrl}
                    {...(!platform.downloadUrl.startsWith('http') ? {} : { target: '_blank' })}
                    disabled={platform.disabled}
                    sx={{
                      py: 1.5,
                      bgcolor: theme.palette.accent?.main || theme.palette.primary.main,
                      color: theme.palette.accent?.contrastText || 'white',
                      '&:hover': {
                        bgcolor: theme.palette.accent?.dark || theme.palette.primary.dark,
                      },
                      '&.Mui-disabled': {
                        bgcolor: 'action.disabledBackground',
                        color: 'action.disabled',
                      },
                    }}
                  >
                    {platform.disabled ? 'Coming Soon' : `Download ${platform.format}`}
                  </Button>
                  
                  {/* CLI Version Button for Windows/Linux */}
                  {platform.cliVersion && (
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      startIcon={<Terminal />}
                      component="a"
                      href={platform.cliVersion.downloadUrl}
                      {...(!platform.cliVersion.downloadUrl.startsWith('http') ? {} : { target: '_blank' })}
                      sx={{ mt: 1 }}
                    >
                      CLI Version ({platform.cliVersion.size})
                    </Button>
                  )}
                  
                  {/* DEB Version Button for Linux */}
                  {platform.debVersion && (
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      component="a"
                      href={platform.debVersion.downloadUrl}
                      sx={{ mt: 1 }}
                    >
                      DEB Package ({platform.debVersion.size})
                    </Button>
                  )}
                  
                  {/* Snap Version Button for Linux */}
                  {platform.snapVersion && (
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      component="a"
                      href={platform.snapVersion.downloadUrl}
                      target="_blank"
                      sx={{ mt: 1 }}
                    >
                      Snap Package ({platform.snapVersion.size})
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Alternative Installation Methods */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
            Alternative Installation
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" paragraph>
            Prefer command line? Install PWGen-Rust using your favorite package manager.
          </Typography>
          
          <Grid container spacing={4} sx={{ mt: 4 }}>
            {installMethods.map((method, index) => (
              <Grid size={{ xs: 12 }} key={index}>
                <Card>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box sx={{ color: theme.palette.accent?.main || theme.palette.primary.main, mr: 2 }}>
                        {method.icon}
                      </Box>
                      <Typography variant="h5" fontWeight="bold">
                        {method.title}
                      </Typography>
                    </Box>
                    
                    <Grid container spacing={3}>
                      {method.methods.map((item, itemIndex) => (
                        <Grid size={{ xs: 12, sm: 6 }} key={itemIndex}>
                          <Box
                            sx={{
                              p: 3,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: theme.palette.accent?.main || theme.palette.primary.main,
                                bgcolor: alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.05),
                              },
                            }}
                          >
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                              {item.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {item.description}
                            </Typography>
                            <Box
                              sx={{
                                bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
                                p: 2,
                                borderRadius: 1,
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                wordBreak: 'break-all',
                              }}
                            >
                              {item.command}
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Source Code Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
            Source Code
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" paragraph>
            PWGen-Rust is open source software. View the source code, contribute, or report issues.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<GitHub />}
              component="a"
              href="https://github.com/KiefStudioMA/PWGen-Rust"
              target="_blank"
              sx={{ px: 4 }}
            >
              View on GitHub
            </Button>
            <Button
              variant="outlined"
              size="large"
              component="a"
              href="https://github.com/KiefStudioMA/PWGen-Rust/releases/latest"
              target="_blank"
              sx={{ px: 4 }}
            >
              Release Notes
            </Button>
          </Box>
        </Box>

        {/* Features Preview */}
        <Box
          sx={{
            textAlign: 'center',
            p: 6,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.accent?.main || theme.palette.secondary.main, 0.1)} 100%)`,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            What's New in v1.2.0
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
            The latest version brings performance improvements and new security features.
          </Typography>
          
          <Grid container spacing={3} justifyContent="center">
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Speed sx={{ fontSize: 48, color: theme.palette.accent?.main || theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  40% Smaller
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  30-40% smaller binaries with optimized build process
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Verified sx={{ fontSize: 48, color: theme.palette.accent?.main || theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  SHA-256 Crypto
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Enhanced security with SHA-256 cryptography implementation
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Smartphone sx={{ fontSize: 48, color: theme.palette.accent?.main || theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Flexible Builds
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Multiple build options: GUI, CLI, and Snap packages
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              startIcon={<GitHub />}
              href="https://github.com/KiefStudioMA/PWGen-Rust/releases/tag/v1.2.0"
              target="_blank"
            >
              View Release Notes
            </Button>
            <Button
              variant="outlined"
              href="https://github.com/KiefStudioMA/PWGen-Rust"
              target="_blank"
            >
              View Source Code
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default DownloadPage;